import { FFHelpers } from '@jlafica/rcform';
import { isDebt } from "../constants/ACCOUNT_TYPE"

export const ACCOUNT_MODEL_TYPES = {
    ACCOUNT_CLIENT: "AccountClientModel",
    ACCOUNT_SERVER: "AccountServerModel",
}

export const getAccountClientModel = (account) => {
    let model = {
        modelName: ACCOUNT_MODEL_TYPES.ACCOUNT_CLIENT,
        id: account.id,
        name: account.name,
        institution: account.institution,
        accountNumber: account.account_number,
        availableBalance: account.available_balance,
        status: account.status,
        type: account.type,
        includeBalance: account.include_balance,
        debtAccountInfo: isDebt(account.type) ? {
            id: account.debt_account_info.id,
            accountId: account.debt_account_info.account_id,
            dueDate: account.debt_account_info.due_date,
            balance: account.debt_account_info.balance,
            lineLimit: account.debt_account_info.line_limit,
            minimumPaymentPercentage: account.debt_account_info.minimum_payment_percentage,
            minimumPaymentLowBalance: account.debt_account_info.minimum_payment_low_balance,
            fixedPayment: account.debt_account_info.fixed_payment,
            introRateEndDate: account.debt_account_info.intro_rate_end_date,
            apr: account.debt_account_info.apr,
            introApr: account.debt_account_info.intro_apr
        } : null,
    }

    return model
}

export const fieldNames = {
    name: 'name',
    institution: 'institution',
    availableBalance: 'availableBalance',
    accountNumber: 'accountNumber',
    status: 'status',
    type: 'type',
    includeBalance: 'includeBalance',
    // Debt Account Info
    balance: 'balance',
    lineLimit: 'lineLimit',
    minimumPaymentPercentage: 'minimumPaymentPercentage',
    minimumPaymentLowBalance: 'minimumPaymentLowBalance',
    fixedPayment: 'fixedPayment',
    introRateEndDate: 'introRateEndDate',
    apr: 'apr',
    introApr: 'introApr',
    dueDate: 'dueDate',
}

export const initialState = {
    [fieldNames.name]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.institution]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.availableBalance]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.accountNumber]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.status]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.type]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.includeBalance]: {
        ...FFHelpers.ffInitialFieldState.checkbox
    },
    // Debt Account Info
    [fieldNames.balance]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.lineLimit]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.minimumPaymentPercentage]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.minimumPaymentLowBalance]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.fixedPayment]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.introRateEndDate]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.apr]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.introApr]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.dueDate]: {
        ...FFHelpers.ffInitialFieldState.input
    },
}

export const getAccountEditFormFieldsModel = (account) => {
    return {
        [fieldNames.name]: {
            value: account.name ?? ''
        },
        [fieldNames.institution]: {
            value: account.institution ?? ''
        },
        [fieldNames.availableBalance]: {
            value: account.availableBalance ?? ''
        },
        [fieldNames.accountNumber]: {
            value: account.accountNumber ?? ''
        },
        [fieldNames.status]: {
            value: account.status ?? ''
        },
        [fieldNames.type]: {
            value: account.type ?? ''
        },
        [fieldNames.includeBalance]: {
            value: account.includeBalance ?? 0,
            checked: account.includeBalance === 1
        },
        // Debt Account Info
        [fieldNames.balance]: {
            value: account.balance ?? ''
        },
        [fieldNames.lineLimit]: {
            value: account.lineLimit ?? ''
        },
        [fieldNames.minimumPaymentPercentage]: {
            value: account.minimumPaymentPercentage ?? ''
        },
        [fieldNames.minimumPaymentLowBalance]: {
            value: account.minimumPaymentLowBalance ?? ''
        },
        [fieldNames.fixedPayment]: {
            value: account.fixedPayment ?? ''
        },
        [fieldNames.introRateEndDate]: {
            value: account.introRateEndDate ?? ''
        },
        [fieldNames.apr]: {
            value: account.apr ?? ''
        },
        [fieldNames.introApr]: {
            value: account.introApr ?? ''
        },
        [fieldNames.dueDate]: {
            value: account.dueDate ?? ''
        },
    }
}

export const getCalculatedAvailableBalance = (fields) => {
    const calculatedAvailableBalance = isDebt(fields[fieldNames.type].value)
        ? fields[fieldNames.lineLimit].value - fields[fieldNames.balance].value
        : fields[fieldNames.availableBalance].value

    return calculatedAvailableBalance
}

export const getAccountSubmitDataFromFields = (fields, id) => {
    return {
        id: id,
        name: fields[fieldNames.name].value,
        type: fields[fieldNames.type].value,
        status: fields[fieldNames.status].value,
        institution: fields[fieldNames.institution].value,
        accountNumber: fields[fieldNames.accountNumber].value,
        includeBalance: fields[fieldNames.includeBalance].checked ? true : false,
        availableBalance: getCalculatedAvailableBalance(fields),
    }
}

export const getDebtAccountSubmitDataFromFields = (fields, id, debtAccountId) => {
    return {
        id: debtAccountId,
        apr: !isNaN(fields[fieldNames.apr].value) ? fields[fieldNames.apr].value : null,
        introApr: fields[fieldNames.introApr].value && !isNaN(fields[fieldNames.introApr].value) ? fields[fieldNames.introApr].value : null,
        balance: parseFloat(fields[fieldNames.balance].value) ?? null,
        dueDate: fields[fieldNames.dueDate].value || null,
        accountId: id,
        lineLimit: fields[fieldNames.lineLimit].value || null,
        fixedPayment: fields[fieldNames.fixedPayment].value || null,
        introRateEndDate: fields[fieldNames.introRateEndDate].value || null,
        minimumPaymentLowBalance: fields[fieldNames.minimumPaymentLowBalance].value || null,
        minimumPaymentPercentage: !isNaN(fields[fieldNames.minimumPaymentPercentage].value) ? fields[fieldNames.minimumPaymentPercentage].value : null,
    }
}

export const getNormalizedRawAccountData = (account) => {
    const normalizedAccountData = {
        id: account.id,
        name: account.name,
        type: account.type,
        status: account.status,
        institution: account.institution,
        includeBalance: account.includeBalance,
        availableBalance: parseFloat(account.availableBalance),
        // Debt Account Info
        apr: account?.debtAccountInfo.apr,
        introApr: account?.debtAccountInfo.introApr,
        balance: account?.debtAccountInfo.balance,
        dueDate: account?.debtAccountInfo.dueDate,
        lineLimit: account?.debtAccountInfo.lineLimit,
        fixedPayment: account?.debtAccountInfo.fixedPayment,
        introRateEndDate: account?.debtAccountInfo.introRateEndDate,
        minimumPaymentLowBalance: account?.debtAccountInfo.minimumPaymentLowBalance,
        minimumPaymentPercentage: account?.debtAccountInfo.minimumPaymentPercentage,
    }

    return normalizedAccountData
}